import { useEmojiShortcode } from '@org/ui';

const oswald = "'Oswald', sans-serif";

/**
 * Selo — the seal stamped on a completed cartaz.
 * Center: the woman's symbol (unit.woman.symbol — an emoji character, or a
 * `:heart:` shortcode as typed in the admin, resolved here).
 * Ring label: the characteristic she embodied (derived from unit.badge).
 * Colors come from the poster's palette so the selo stays tethered to its cartaz.
 */
export function Selo({
  symbol,
  label,
  background,
  size = 56,
  rotate = 12,
  style,
}: {
  symbol: string | null | undefined;
  label: string | null | undefined;
  background: string;
  size?: number;
  rotate?: number;
  style?: React.CSSProperties;
}) {
  const glyph = useEmojiShortcode(symbol);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background,
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: `${Math.max(2, size * 0.036)}px solid #FFFFFF`,
        outline: `2px solid ${background}`,
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.3)',
        transform: `rotate(${rotate}deg)`,
        fontFamily: oswald,
        position: 'relative',
        flexShrink: 0,
        ...style,
      }}
    >
      {/* inner dashed ring, like an ink stamp */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: Math.max(3, size * 0.06),
          borderRadius: '50%',
          border: '1.2px dashed rgba(255, 255, 255, 0.55)',
        }}
      />
      <span style={{ fontSize: size * 0.44, lineHeight: 1 }}>
        {glyph || '✦'}
      </span>
      {label && (
        <span
          style={{
            fontSize: Math.max(7, size * 0.13),
            fontWeight: 700,
            letterSpacing: '0.06em',
            marginTop: Math.max(1, size * 0.03),
            maxWidth: '88%',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

/**
 * Derive the characteristic word from a badge name:
 * "Voice Badge" -> "Voice", "Selo da Coragem" -> "Coragem".
 * Falls back to the unit name when the badge has no usable word.
 */
export function seloCharacteristic(
  badge: string | null | undefined,
  fallback: string | null | undefined,
): string {
  const stripped = (badge ?? '')
    .replace(/\b(badge|selo|seal)\b/gi, '')
    .replace(/\b(da|de|do|of|the)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped || (fallback ?? '');
}
